<template>
    <AppLayout title="Revisi Penyusunan Dokumen Pemilihan">
        <Toast />
        <div class="relative">
            <div class="py-4" :class="{ 'pointer-events-none': loading }">
                <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
                    <div class="bg-white overflow-hidden shadow-xl sm:rounded-lg">
                        <section class="text-gray-600 body-font">
                            <div class="container px-5 py-5 mx-auto">
                                <div class="flex flex-wrap w-full mb-5 flex-col">
                                    <!-- Alert for file deletion warning -->
                                    <div class="mb-4 p-4 bg-yellow-50 border-l-4 border-yellow-500 text-yellow-700">
                                        <div class="flex">
                                            <div class="flex-shrink-0">
                                                <i class="pi pi-exclamation-triangle mr-2"></i>
                                            </div>
                                            <div>
                                                <p class="font-medium">Perhatian!</p>
                                                <p>File lama akan dihapus secara permanen ketika Anda mengunggah file baru. Pastikan file baru sudah siap sebelum melanjutkan.</p>
                                            </div>
                                        </div>
                                    </div>

                                    <Accordion
                                        :multiple="true"
                                        :activeIndex="[0, 1, 2]"
                                    >
                                        <AccordionTab>
                                            <template #header>
                                                <span class="flex align-items-center gap-2 w-full">
                                                    <span class="font-bold white-space-nowrap">Status Dokumen</span>
                                                </span>
                                            </template>
                                            <StatusDokumenPengadaanInfo
                                                :status="dataViewDokumenPengadaan.status"
                                            />
                                            <ViewAttribute
                                                :dataViewDokumenPengadaan="dataViewDokumenPengadaan"
                                            />
                                        </AccordionTab>

                                        <!-- File Upload Section -->
                                        <AccordionTab>
                                            <template #header>
                                                <span class="flex align-items-center gap-2 w-full">
                                                    <span class="font-bold white-space-nowrap">Dokumen Pemilihan</span>
                                                    <span class="text-red-500 text-sm ml-2">*Revisi</span>
                                                </span>
                                            </template>
                                            
                                            <!-- Current Files Display -->
                                            <div v-if="dataViewDokumenPengadaan.dokumen_pemilihan_file.length > 0" class="mb-4">
                                                <h6 class="text-sm font-medium text-gray-700 mb-2">File Saat Ini:</h6>
                                                <FileDisplay
                                                    :existing_files="dataViewDokumenPengadaan.dokumen_pemilihan_file"
                                                />
                                            </div>

                                            <!-- File Size Info -->
                                            <div class="mb-3 p-3 border-l-4 border-blue-500 bg-blue-50">
                                                <div class="flex items-center">
                                                    <i class="pi pi-info-circle text-blue-600 mr-2"></i>
                                                    <span class="text-blue-700">Ukuran maksimal file: <strong>10 MB</strong></span>
                                                </div>
                                            </div>
                                            
                                            <!-- New File Upload -->
                                            <FileInput 
                                                :file_uploads="form.file_uploads" 
                                                :exlude_hps="true" 
                                                :required="false" 
                                                revision_mode="true" />
                                        </AccordionTab>

                                        <!-- Jadwal Section -->
                                        <AccordionTab v-if="dataViewDokumenPengadaan.config.includes('jadwal')">
                                            <template #header>
                                                <span class="flex align-items-center gap-2 w-full">
                                                    <span class="font-bold white-space-nowrap">Jadwal</span>
                                                    <span class="text-red-500 text-sm ml-2">*Revisi</span>
                                                </span>
                                            </template>
                                            <div class="mb-3 p-3 border-l-4 border-orange-500 bg-orange-50">
                                                <div class="flex items-center">
                                                    <i class="pi pi-clock text-orange-600 mr-2"></i>
                                                    <span class="text-orange-700">
                                                        Perubahan jadwal "{{ timeLockScheduleName }}" akan memperbarui waktu pembukaan dokumen secara otomatis.
                                                    </span>
                                                </div>
                                            </div>
                                            <JadwalInput
                                                v-model:jadwals="form.jadwal"
                                            />
                                        </AccordionTab>

                                        <!-- Display-only sections -->
                                        <AccordionTab v-if="dataViewDokumenPengadaan.config.includes('detail_hps')">
                                            <template #header>
                                                <span class="flex align-items-center gap-2 w-full">
                                                    <span class="font-bold white-space-nowrap">Detail HPS</span>
                                                    <span class="text-gray-500 text-sm ml-2">(Hanya Tampilan)</span>
                                                </span>
                                            </template>
                                            <DisplayHps
                                                :detail_hps="dataViewDokumenPengadaan.detail_hps"
                                            />
                                        </AccordionTab>
                                    </Accordion>

                                    <div class="flex justify-content-between mt-4">
                                        <Button 
                                            icon="pi pi-arrow-left"
                                            label="Kembali"
                                            @click="goBack"
                                            class="p-button-secondary" />
                                        <Button 
                                            icon="pi pi-save"
                                            label="Simpan Revisi"
                                            @click="initiateSubmit"
                                            class="p-button-primary" />
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </div>
            <div v-if="loading" class="loading-overlay">
                <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="3" />
                <div class="loading-text">Memproses revisi...</div>
            </div>
        </div>

        <Dialog 
            v-model:visible="showConfirmDialog" 
            :modal="true"
            :closable="false"
            header="Konfirmasi Revisi">
            <div class="p-4">
                <p>Apakah Anda yakin ingin menyimpan revisi dokumen ini?</p>
                <div v-if="hasFileUploads" class="mt-2 text-yellow-600">
                    <i class="pi pi-exclamation-triangle mr-2"></i>
                    <span>File lama akan dihapus dan diganti dengan file baru.</span>
                </div>
                <div v-if="hasJadwalChanges" class="mt-2 text-blue-600">
                    <i class="pi pi-clock mr-2"></i>
                    <span>Jadwal akan diperbarui sesuai dengan perubahan yang dibuat.</span>
                </div>
                <div class="mt-4">
                    <p v-if="cooldownActive" class="text-sm text-gray-500">
                        Mohon tunggu {{ currentCountdown }} detik sebelum konfirmasi...
                    </p>
                </div>
            </div>
            <template #footer>
                <Button 
                    label="Batal" 
                    @click="cancelSubmit" 
                    class="p-button-text" />
                <Button 
                    label="Simpan Revisi"
                    @click="confirmSubmit"
                    :disabled="cooldownActive"
                    class="p-button-primary" />
            </template>
        </Dialog>
    </AppLayout>
</template>

<script>
import { reactive, ref, computed, getCurrentInstance } from "vue";
import AppLayout from "@/Layouts/AppLayout.vue";
import Accordion from "primevue/accordion";
import AccordionTab from "primevue/accordiontab";
import StatusDokumenPengadaanInfo from "@/Components/StatusDokumenPengadaanInfo";
import FileDisplay from "./partials/FileDisplay.vue";
import ViewAttribute from "./partials/ViewAttribute.vue";
import DisplayHps from "./partials/DisplayHps.vue";
import JadwalInput from "./partials/JadwalInput.vue";
import FileInput from "./partials/FileInput.vue";
import { Inertia } from '@inertiajs/inertia';
import Toast from 'primevue/toast';
import { useToast } from "primevue/usetoast";
import ProgressSpinner from 'primevue/progressspinner';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';

export default {
    components: {
        AppLayout,
        Accordion,
        AccordionTab,
        StatusDokumenPengadaanInfo,
        FileDisplay,
        ViewAttribute,
        DisplayHps,
        JadwalInput,
        FileInput,
        Toast,
        ProgressSpinner,
        Dialog,
        Button,
    },
    props: {
        dataViewDokumenPengadaan: Object,
        file_input_pemilihan: Array,
        errors: Object,
        back_url_param: Object,
    },
    setup(props) {
        const toast = useToast();
        const loading = ref(false);
        const showConfirmDialog = ref(false);
        const cooldownActive = ref(false);
        const currentCountdown = ref(3);
        const vm = getCurrentInstance().proxy;

        const form = reactive({
            jadwal: [],
            file_uploads: [],
        });

        const timeLockScheduleName = computed(() => {
            const timeLockKode = 'penawaran_pengadaan'; // from config
            const jadwal = props.dataViewDokumenPengadaan?.jadwal?.find(j => j.kode_jadwal === timeLockKode);
            return jadwal?.nama_jadwal || 'Penawaran Pengadaan';
        });

        const hasFileUploads = computed(() => {
            return form.file_uploads.some(upload => upload.new_file && upload.new_file.length > 0);
        });

        const hasJadwalChanges = computed(() => {
            return form.jadwal.some(jadwal => jadwal.existing_jadwal);
        });

        const initiateSubmit = () => {
            showConfirmDialog.value = true;
            cooldownActive.value = true;
            
            currentCountdown.value = 3;
            const timer = setInterval(() => {
                currentCountdown.value--;
                if (currentCountdown.value <= 0) {
                    clearInterval(timer);
                    cooldownActive.value = false;
                }
            }, 1000);
        };

        const confirmSubmit = () => {
            if (!cooldownActive.value) {
                showConfirmDialog.value = false;
                submit();
            }
        };

        const cancelSubmit = () => {
            showConfirmDialog.value = false;
            cooldownActive.value = false;
            currentCountdown.value = 3;
        };

        const submit = () => {
            loading.value = true;
            
            Inertia.post(route("dokumen_pengadaan.action_revisi_penyusunan_dokumen_pemilihan"), {
                uuid: props.dataViewDokumenPengadaan.uuid,
                jadwal: form.jadwal,
                file_uploads: form.file_uploads,
            }, {
                preserveScroll: true,
                onSuccess: (page) => {
                    loading.value = false;
                    if (page.props.flash.error) {
                        toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: page.props.flash.error,
                            life: 5000
                        });
                    } else if (page.props.flash.success) {
                        toast.add({
                            severity: 'success',
                            summary: 'Berhasil',
                            detail: page.props.flash.success,
                            life: 3000
                        });
                    }
                },
                onError: (errors) => {
                    loading.value = false;
                    let errorMessage = 'Terjadi kesalahan saat menyimpan revisi';
                    
                    if (errors.file_uploads) {
                        errorMessage = errors.file_uploads;
                    } else if (errors.jadwal) {
                        errorMessage = errors.jadwal;
                    }
                    
                    toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: errorMessage,
                        life: 5000
                    });
                },
                onFinish: () => {
                    loading.value = false;
                }
            });
        };

        const goBack = () => {
            Inertia.visit(route('dokumen_pengadaan.list_revisi_penyusunan_dokumen_pemilihan'));
        };

        return {
            form,
            loading,
            showConfirmDialog,
            cooldownActive,
            currentCountdown,
            timeLockScheduleName,
            hasFileUploads,
            hasJadwalChanges,
            initiateSubmit,
            confirmSubmit,
            cancelSubmit,
            submit,
            goBack,
        };
    },
    mounted() {
        console.log("RevisiPenyusunanDokumenPemilihanForm mounted", this.dataViewDokumenPengadaan);
        
        // Initialize file uploads if file_input_pemilihan is provided
        if (this.file_input_pemilihan && Array.isArray(this.file_input_pemilihan)) {
            this.file_input_pemilihan.forEach((file) => {
                this.form.file_uploads.push({
                    id_file_dokumen_pengadaan: file.id,
                    id_jadwal_pengadaan: file.id_jadwal_pengadaan,
                    id_dokumen_pengadaan: file.id_dokumen_pengadaan,
                    id_file_dokumen_prosedur: file.id_file_dokumen_prosedur,
                    kode_file: file.kode_file,
                    kode_prosedur: file.kode_prosedur,
                    nama_file: file.nama_file,
                    file_path: file.file_path,
                    file_type: file.file_type,
                    file_dokumen_type: file.file_dokumen_type,
                    file_dokumen_type_id: file.file_dokumen_type_id,
                    multiple: file.is_multiple === 1,
                    new_file: [],
                });
            });
        }
        
        // Initialize jadwal from dataViewDokumenPengadaan if available
        if (this.dataViewDokumenPengadaan?.jadwal) {
            this.form.jadwal = this.dataViewDokumenPengadaan.jadwal.map((jadwal) => {
                return {
                    ...jadwal,
                    existing_jadwal: jadwal.existing_jadwal || {
                        waktu_mulai: '',
                        waktu_selesai: '',
                        keterangan: ''
                    }
                };
            });
        }
    },
};
</script>

<style scoped>
.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.8);
    z-index: 50;
}

.loading-text {
    margin-top: 1rem;
    color: var(--primary-color);
    font-size: 1rem;
    font-weight: 500;
}

.pointer-events-none {
    pointer-events: none;
    opacity: 0.7;
}

:deep(.p-toast-message-warn) {
    background-color: #fef3c7 !important;
    border: solid #fcd34d;
    border-width: 0 0 0 6px;
    color: #92400e !important;
}

:deep(.p-toast-message-warn .p-toast-message-icon),
:deep(.p-toast-message-warn .p-toast-icon-close) {
    color: #92400e !important;
}
</style> 